import { Module } from "../lib/modules";
import { StateManager } from "../lib/stateManager";
import { calculateGameRating, CategoryName, CategoryScores } from "../utils/rating";

type RatingState = {
  [key in CategoryName]: number;
};

// "gameplay"
// "graphics"
// "visualDesign"
// "audio"
// "music"
// "narrative"
// "optimization"
// "stability"
// "trueToVision"
// "innovation"
// "longevity"
// "multiplayer"
// "scale"
// "value";

type CategorySlider = {
  name: CategoryName;
  slider: HTMLInputElement;
  value: HTMLSpanElement;
};

interface DOMElements {
  // Sliders
  categories: CategorySlider[];
  overallRating: HTMLSpanElement;
}

export const Rating: Module<RatingState> = () => {
  // Create a state manager with initial state
  const state = new StateManager<RatingState>({
    gameplay: 0,
    graphics: 0,
    visualDesign: 0,
    audio: 0,
    music: 0,
    narrative: 0,
    optimization: 0,
    stability: 0,
    trueToVision: 0,
    innovation: 0,
    longevity: 0,
    multiplayer: 0,
    scale: 0,
    value: 0
  });

  let elements: DOMElements | null;

  const init = () => {
    elements = {
      categories: [
        {
          name: "gameplay",
          slider: document.querySelector("#gameplay") as HTMLInputElement,
          value: document.querySelector("#gameplay-value") as HTMLSpanElement
        },
        {
          name: "graphics",
          slider: document.querySelector("#graphics") as HTMLInputElement,
          value: document.querySelector("#graphics-value") as HTMLSpanElement
        },
        {
          name: "visualDesign",
          slider: document.querySelector("#visual-design") as HTMLInputElement,
          value: document.querySelector("#visual-design-value") as HTMLSpanElement
        },
        {
          name: "audio",
          slider: document.querySelector("#audio") as HTMLInputElement,
          value: document.querySelector("#audio-value") as HTMLSpanElement
        },
        {
          name: "music",
          slider: document.querySelector("#music") as HTMLInputElement,
          value: document.querySelector("#music-value") as HTMLSpanElement
        },
        {
          name: "narrative",
          slider: document.querySelector("#narrative") as HTMLInputElement,
          value: document.querySelector("#narrative-value") as HTMLSpanElement
        },
        {
          name: "optimization",
          slider: document.querySelector("#optimization") as HTMLInputElement,
          value: document.querySelector("#optimization-value") as HTMLSpanElement
        },
        {
          name: "stability",
          slider: document.querySelector("#stability") as HTMLInputElement,
          value: document.querySelector("#stability-value") as HTMLSpanElement
        },
        {
          name: "trueToVision",
          slider: document.querySelector("#true-to-vision") as HTMLInputElement,
          value: document.querySelector("#true-to-vision-value") as HTMLSpanElement
        },
        {
          name: "innovation",
          slider: document.querySelector("#innovation") as HTMLInputElement,
          value: document.querySelector("#innovation-value") as HTMLSpanElement
        },
        {
          name: "longevity",
          slider: document.querySelector("#longevity") as HTMLInputElement,
          value: document.querySelector("#longevity-value") as HTMLSpanElement
        },
        {
          name: "multiplayer",
          slider: document.querySelector("#multiplayer") as HTMLInputElement,
          value: document.querySelector("#multiplayer-value") as HTMLSpanElement
        },
        {
          name: "scale",
          slider: document.querySelector("#scale") as HTMLInputElement,
          value: document.querySelector("#scale-value") as HTMLSpanElement
        },
        {
          name: "value",
          slider: document.querySelector("#value") as HTMLInputElement,
          value: document.querySelector("#value-value") as HTMLSpanElement
        }
      ],
      overallRating: document.querySelector("#overall-rating") as HTMLSpanElement
    };

    attachEventListeners();
  };

  const updateOverallRating = () => {
    if (!elements) return;

    const scores: CategoryScores = {
      gameplay: state.get("gameplay") / 10,
      graphics: state.get("graphics") / 10,
      visualDesign: state.get("visualDesign") / 10,
      audio: state.get("audio") / 10,
      music: state.get("music") / 10,
      narrative: state.get("narrative") / 10,
      optimization: state.get("optimization") / 10,
      stability: state.get("stability") / 10,
      trueToVision: state.get("trueToVision") / 10,
      innovation: state.get("innovation") / 10,
      longevity: state.get("longevity") / 10,
      multiplayer: state.get("multiplayer") / 10,
      value: state.get("value") / 10,
      scale: state.get("scale") / 10
    };

    const score = calculateGameRating(scores).toFixed(1);
    elements.overallRating.innerText = score;
  };

  const handleSliderChange = (slider: CategorySlider) => {
    const value = parseInt(slider.slider.value);
    // const adjustedScore = getWeightedScore(categories[slider.name].curve, value / 10);
    slider.value.innerText = (value / 10).toFixed(1);
    state.set(slider.name, value);
    updateOverallRating();
  };

  const attachEventListeners = () => {
    if (!elements) return;

    elements.categories.forEach((category) => {
      category.slider.addEventListener("input", () => handleSliderChange(category));
      handleSliderChange(category); // Set initial value
    });
  };

  const detachEventListeners = () => { };

  const destroy = () => {
    detachEventListeners();
  };

  return {
    init,
    attachEventListeners,
    detachEventListeners,
    destroy,
    state,
    name: "rating",
    pages: ["/rating"]
  };
};
